import { useMemo, useState } from 'react';
import { AgentEditor, DEFAULT_AGENT_CONFIG, AgentConfig } from 'peak-editor-components';

interface LLMConfigTabProps {
  data: any;
  onChange: (key: string, value: any) => void;
}

const COMMON_PROPS = {
    variables: { user: { query: 'Hello' } },
    variablesJson: JSON.stringify({ user: { query: 'Hello' } }, null, 2),
    onVariablesChange: () => {},
    variableError: null,
    functions: [],
    onFunctionsChange: () => {}
};

export const LLMConfigTab: React.FC<LLMConfigTabProps> = ({ data, onChange }) => {
  const [isRunning, setIsRunning] = useState(false);

  // Construct config object from data props (stateless / controlled)
  const config: AgentConfig = useMemo(() => ({
      ...DEFAULT_AGENT_CONFIG,
      id: 'agent',
      name: data.label || 'Workflow AI Node',
      modelId: data.model || 'gemini-2.5-flash',
      systemMessage: data.systemPrompt || 'You are a helpful assistant.',
      userMessageInput: data.userPrompt || '',
      provider: data.provider || 'google',
      temperature: data.temperature ?? 0.7,
      jsonMode: data.jsonMode ?? false,
      outputParser: data.outputParser || 'TEXT',
  }), [
      data.label,
      data.model,
      data.systemPrompt,
      data.userPrompt,
      data.provider,
      data.temperature,
      data.jsonMode,
      data.outputParser
  ]);

  const handleConfigChange = useMemo(() => (newConfig: AgentConfig) => {
      // Map relevant fields back to the workflow node data
      onChange('model', newConfig.modelId);
      onChange('systemPrompt', newConfig.systemMessage);
      onChange('userPrompt', newConfig.userMessageInput);
      onChange('provider', newConfig.provider);
      onChange('temperature', newConfig.temperature);
      onChange('jsonMode', newConfig.jsonMode);
      onChange('outputParser', newConfig.outputParser);

      // Auto-set output variable based on agent name if not already set
      const safeName = (typeof newConfig.name === 'string' ? newConfig.name : '').replace(/[^a-zA-Z0-9]/g, '');
      const resultVar = safeName ? `${safeName}Result` : 'resultVariable';
      onChange('outputVariable', resultVar);

      if (newConfig.name !== data.label) {
          onChange('label', newConfig.name);
      }
  }, [onChange, data.label]);

  const handleRun = () => {
      setIsRunning(true);
      setTimeout(() => setIsRunning(false), 2000);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-1 relative min-h-[400px]">
        <AgentEditor
            config={config}
            onChange={handleConfigChange}
            onRun={handleRun}
            isRunning={isRunning}
            runError={null}
            {...COMMON_PROPS}
        />
      </div>
    </div>
  );
};
